import { UserRole } from "../../types";

/**
 * One hostname a clinic answers on.
 *
 * A visitor to it sees that clinic's colours, logo, About page and price list before they
 * sign in, and is never shown that the platform exists. Which is why only the platform owner
 * may set one: a hostname is globally unique, so claiming it takes it from every other clinic.
 */
export interface IClinicDomain {
    id: number;
    /** Stored normalised: lowercase, no scheme, no port, no leading `www.`. */
    hostname: string;
    /** The one this clinic's emailed links name. At most one per clinic, sometimes none. */
    primary: boolean;
}

/**
 * A clinic. The API calls these tenants; the word stays inside the service layer, and
 * everything the user sees says clinic.
 */
export interface IClinic {
    id: number;
    /** Immutable after creation -- there is no way to change it through the API. */
    slug: string;
    displayName: string;
    active: boolean;
    /**
     * The address this clinic's emails are sent from, or null to send from the platform's.
     * Only the platform owner sets it, and only once the address's domain is authenticated in
     * Brevo -- Brevo rejects a send from any other, so a wrong value stops every email the
     * clinic sends.
     */
    senderEmail: string | null;
    createdAt: string;
    updatedAt: string;
    /**
     * Primary first, then alphabetical, as the API orders them -- which is what lets the
     * table and the edit dialog take the head of the list and call it the clinic's address.
     * Empty for a clinic that has none, never absent.
     */
    domains: IClinicDomain[];
}

/**
 * What passes for a hostname before the API sees it.
 *
 * Deliberately permissive, for the reason `phoneNumber` gives: the API accepts whatever shape
 * it was typed -- `https://www.MotionCare.ro/` included -- and `Hostnames.normalise` decides
 * the stored form, so a form that insisted on the bare apex would refuse what the server was
 * about to accept. It catches the two mistakes that matter: a name with no dot, and one with
 * whitespace in it.
 */
const HOSTNAME_PATTERN: RegExp = /^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(:\d+)?(\/.*)?$/i;

export const isUsableHostname = (value: string): boolean => HOSTNAME_PATTERN.test(value.trim());

/** The API caps the field at 300, which allows for a pasted `https://` and trailing slash. */
export const HOSTNAME_MAX: number = 300;

/**
 * A clinic's address as a list shows it: the primary hostname, with a suffix when there are
 * more behind it. Empty for a clinic that answers on nothing yet.
 *
 * Here rather than in the table so the table and the compact card cannot describe the same
 * clinic differently -- the argument `Clinics.tsx` already makes for its shared row actions.
 */
export const primaryDomainLabel = (clinic: IClinic, moreSuffix: string): string => {
    if (clinic.domains.length === 0) {
        return '';
    }

    return clinic.domains.length === 1
        ? clinic.domains[0].hostname
        : `${clinic.domains[0].hostname}${moreSuffix}`;
};

/**
 * No opening hours. When a clinic is open is its own business and its administrator sets it
 * on their settings screen -- see `clinicProgram`. A new clinic starts open Monday to Friday.
 */
export interface CreateClinicRequest {
    slug: string;
    displayName: string;
    /**
     * Optional, and registered in the same transaction as the clinic. A hostname the API
     * refuses -- malformed, or already another clinic's -- fails the creation outright, so
     * there is never a clinic left behind without the domain it was created for.
     */
    hostname?: string;
}

/** Every field is optional: an absent one means "leave it alone". */
export interface UpdateClinicRequest {
    displayName?: string;
    active?: boolean;
    /** `''` clears it, putting the clinic back on the platform's sender address. */
    senderEmail?: string;
}

/**
 * `hostname` is only ever filled in while creating. An existing clinic's domains are managed
 * one at a time against their own endpoints -- see `ClinicDomains` -- because adding, removing
 * and re-pointing them are not draft state: each takes effect the moment it is asked for.
 */
export interface ClinicFormValues {
    slug: string;
    displayName: string;
    hostname: string;
    /**
     * Only ever filled in while editing, and seeded from the clinic -- unlike `hostname` -- so
     * that saving a rename does not clear it. Blank means the platform's address.
     */
    senderEmail: string;
}

export const toCreateClinicRequest = (values: ClinicFormValues): CreateClinicRequest => ({
    slug: values.slug.trim(),
    displayName: values.displayName.trim(),
    // Dropped rather than sent as "", which the API would take for a hostname and reject.
    hostname: values.hostname.trim() || undefined,
});

/** What the owner types to give an existing clinic another hostname. */
export interface AddClinicDomainRequest {
    /**
     * Accepted in whatever shape it was typed -- `https://www.MotionCare.ro/` included. The
     * API normalises it, so this deliberately does not.
     */
    hostname: string;
    /** Ignored for a clinic with no domains yet: the first one always becomes primary. */
    primary: boolean;
}

/**
 * Deliberately omits `slug`, which the API will not update, `hostname`, which is not part of
 * a PATCH at all, and `active`, which only the deactivate/reactivate action may change -- otherwise a form opened before a status
 * change would put the old status back on save.
 *
 * `senderEmail` is always sent, blank included: the form was seeded with the clinic's current
 * address, so what it holds on save is the owner's answer, and `''` is how the API is told to
 * clear it.
 */
export const toUpdateClinicRequest = (values: ClinicFormValues): UpdateClinicRequest => ({
    displayName: values.displayName.trim(),
    senderEmail: values.senderEmail.trim(),
});

/**
 * What a membership currently amounts to. Derived on the server from the membership row
 * and any outstanding invitation, because neither alone can tell an unanswered
 * invitation from a revocation.
 */
export type MembershipStatus = 'ACTIVE' | 'PENDING' | 'INVITATION_EXPIRED' | 'REVOKED';

/**
 * Somebody attached to a clinic: the account, plus what it is to that clinic. The same
 * shape whether the platform owner is looking into a clinic or a clinic admin is looking
 * at their own.
 */
export interface IClinicMember {
    id: number;
    emailAddress: string;
    firstName?: string;
    lastName?: string;
    /** Absent until the person fills it in on their own profile. */
    phoneNumber?: string;
    /**
     * Where their profile picture is served from, or absent when they have none.
     *
     * Relative to the API; `absoluteUserPictureUrl` makes it fetchable, and `UserAvatar`
     * draws their initial for everyone who has not set one -- which is most people.
     */
    pictureUrl?: string | null;
    createdAt: string;
    /** Null only if a membership somehow carries no role at all. */
    role: UserRole | null;
    status: MembershipStatus;
    invitedAt: string;
    deactivatedAt?: string;
}

/**
 * A therapist a patient belongs to, and why they belong to them.
 *
 * The two flags are not exclusive and at least one is always true. A therapist who
 * evaluated a patient and also provides their service appears once with both set, rather
 * than twice -- they are one person, however many ways they arrived.
 */
export interface IPatientTherapist {
    /** The therapist's user id: a therapist is a user, not a record of their own. */
    id: number;
    firstName?: string;
    lastName?: string;
    /**
     * Where their profile picture is served from, or absent when they have none.
     *
     * Relative to the API; `absoluteUserPictureUrl` makes it fetchable, and `UserAvatar`
     * draws their initial for everyone who has not set one -- which is most people.
     */
    pictureUrl?: string | null;
    /** They performed at least one of this patient's evaluations. */
    viaEvaluation: boolean;
    /** They provide at least one of the services this patient bought. */
    viaService: boolean;
}

/**
 * A patient, with the therapists they belong to.
 *
 * Widens IClinicMember rather than replacing it so every membership helper --
 * memberFullName, the status chip, toResendInvitationRequest -- keeps working unchanged.
 * The extra field only exists on the patients endpoint, because who treats whom is
 * meaningless for the therapists and admins the member listings also return.
 */
export interface IPatient extends IClinicMember {
    /** Empty when nobody has claimed them: invited by an admin, never evaluated. */
    therapists: IPatientTherapist[];
}

/**
 * What a therapist sends to add a patient to their clinic.
 *
 * Also how an invitation is re-sent: the API has no separate resend endpoint because
 * there is nothing separate about it. Inviting an address that was already invited
 * reuses the account, the membership and the code rather than making second ones, and
 * sends the same email with the same link, which has no expiry. Only an already-active
 * member is a no-op.
 */
export interface InviteCustomerRequest {
    emailAddress: string;
    firstName?: string;
    lastName?: string;
    /**
     * Optional, and the API only ever fills a gap with it: a number the patient set on
     * their own profile is never overwritten by one typed at a reception desk.
     */
    phoneNumber?: string;
}

/**
 * The add-patient form, which is the same four fields wherever it is opened from -- the
 * Patients page or the booking picker.
 *
 * Every field is a string, blank meaning "not given", so the form has one representation
 * of an empty input and `toInviteCustomerRequest` is the only place that decides what an
 * empty one means on the wire.
 */
export interface AddPatientFormValues {
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

/** Blanks are dropped rather than sent as `''`, which the API would store as a name. */
export const toInviteCustomerRequest = (values: AddPatientFormValues): InviteCustomerRequest => ({
    emailAddress: values.emailAddress.trim(),
    firstName: values.firstName.trim() || undefined,
    lastName: values.lastName.trim() || undefined,
    phoneNumber: values.phoneNumber.trim() || undefined,
});

/** What the add-patient form starts with, every time it opens: nothing. */
export const addPatientDefaults: AddPatientFormValues = {
    emailAddress: '', firstName: '', lastName: '', phoneNumber: '',
};

/**
 * The invitation to send somebody who is already on the list. Their name is echoed back
 * rather than left out: the API takes it to fill in an account nobody has claimed yet,
 * and omitting it on the second attempt would blank what the first one recorded.
 */
export const toResendInvitationRequest = (member: IClinicMember): InviteCustomerRequest => ({
    emailAddress: member.emailAddress,
    firstName: member.firstName || undefined,
    lastName: member.lastName || undefined,
});

/** First and last name are optional -- the invitee fills in what is missing. */
export interface InviteAdminRequest {
    emailAddress: string;
    firstName?: string;
    lastName?: string;
}

/** What the invite form holds. Blank optional fields are dropped rather than sent as "". */
export interface InviteAdminFormValues {
    emailAddress: string;
    firstName: string;
    lastName: string;
}

export const toInviteAdminRequest = (values: InviteAdminFormValues): InviteAdminRequest => ({
    emailAddress: values.emailAddress.trim(),
    firstName: values.firstName.trim() || undefined,
    lastName: values.lastName.trim() || undefined,
});
